import React, { useState, useEffect, useCallback } from 'react';
import { Typography, Box, Button } from '@mui/material';
import './ReportDetails.css';
import Layout from '../../../../../components/Layout';
import AdminNavbar from '../../../../../components/Admin/AdminNavbar/AdminNavbar';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import DisputeResolveModal from '../../../../../components/Admin/Payments/Disputes/DisputeResolveModel/DisputeResolveModal';
import DisputeRejectModal from '../../../../../components/Admin/Payments/Disputes/DisputeRejectModel/DisputeRejectModal';
import TeacherEnableModal from '../../../../../components/Admin/UserManagement/Teacher/TeacherEnableModal/TeacherEnableModal';
import StudentEnableModal from '../../../../../components/Admin/UserManagement/Student/StudentEnableModal/StudentEnableModal';
import ReportActionConfirmModal from '../../../../../components/Admin/Payments/Disputes/ReportActionConfirmModal';
import { axiosInstance } from '../../../../../utils/tokenUtils';
import { formatDateFull, toLocalTime } from '../../../../../utils/dateUtils';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ChatIcon from '@mui/icons-material/Chat';
import KoinLogo from '../../../../../assets/Admin/Dispute/koinlogo.svg';
import { toast } from 'react-hot-toast';
import DisputeDetailsSkeleton from '../../../../../components/SkeletonEffect/DisputeDetailsSkeleton/DisputeDetailsSkeleton';
import SessionRecordings from '../../../../../components/Admin/SessionRecordings/SessionRecordings';

const UserRoleColor = {
  Student: { color: '#6B60E7', bgcolor: '#F0EFFF', value: 'Learner' },
  Teacher: { color: '#BC8D00', bgcolor: '#FFF3CE', value: 'Instructor' },
};

const StatusColor = {
  pending: { color: '#E0AD14', bgcolor: '#FFFCF2', value: 'Pending' },
  resolved: { color: '#14B334', bgcolor: '#F2FAF3', value: 'Resolved' },
  rejected: { color: '#E53535', bgcolor: '#FFF2F2', value: 'Rejected' },
};

const formatText = text => {
  if (!text) return '';
  return text.replace(/_/g, ' ').replace(/^\w/, char => char.toUpperCase());
};

const ReportDetails = () => {
  const [openResolveModal, setResolveOpen] = useState(false);
  const [openRejectModal, setRejectOpen] = useState(false);
  const [reportDetails, setReportDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [riyalConversionRate, setRiyalConversionRate] = useState(null);
  const [chatHistory, setChatHistory] = useState(null);
  const [chatLoading, setChatLoading] = useState(false);
  const [rbacMe, setRbacMe] = useState(null);
  const [openTeacherDisableModal, setOpenTeacherDisableModal] = useState(false);
  const [openStudentDisableModal, setOpenStudentDisableModal] = useState(false);
  const [studentTarget, setStudentTarget] = useState(null);
  const [openActionConfirm, setOpenActionConfirm] = useState(false);
  const [actionTarget, setActionTarget] = useState(null);

  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const fromReportTab = location.state?.fromReportTab;

  // Format report ID to display format (full UUID)
  const formatReportId = reportId => {
    if (!reportId) return '#N/A';
    return `#${reportId.toUpperCase()}`;
  };

  const hasPermission = permission => {
    if (!rbacMe || !Array.isArray(rbacMe.permissions)) return false;
    return rbacMe.permissions.includes(permission);
  };

  const canManageDisputes = () => {
    return hasPermission('manage_disputes') || (rbacMe?.roles || []).includes('super_admin');
  };

  useEffect(() => {
    const fetchCurrentUser = async () => {
      try {
        let cachedData = null;
        try {
          const cached = sessionStorage.getItem('rbac_admin_me');
          if (cached) {
            cachedData = JSON.parse(cached);
            setRbacMe(cachedData);
          }
        } catch (_) {}

        const { data } = await axiosInstance.get('/admin/rbac/admin/me');
        setRbacMe(data);

        try {
          sessionStorage.setItem('rbac_admin_me', JSON.stringify(data));
        } catch (_) {}
      } catch (_) {
        if (!rbacMe) setRbacMe({ roles: [], permissions: [] });
      }
    };

    fetchCurrentUser();
  }, []);

  const fetchReportDetails = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(`/admin/report-session/${id}`);
      setReportDetails(response.data);
      setLoading(false);
    } catch (err) {
      setError('Failed to load report details');
      setLoading(false);
    }
  }, [id]);

  const fetchConversionRate = useCallback(async () => {
    try {
      const response = await axiosInstance.get('/auth/koins-to-riyal');
      setRiyalConversionRate(response.data.conversion_rate);
    } catch (_) {}
  }, []);

  const fetchChatHistory = useCallback(async () => {
    const teacherId = reportDetails?.booking?.teacher?.id;
    let learnerId = null;

    if (!teacherId) {
      return;
    }

    const userRole = (reportDetails?.user_role || '').toLowerCase();

    if (userRole === 'student') {
      learnerId = reportDetails?.reporter?.id || null;
    } else if (
      Array.isArray(reportDetails?.reported_users) &&
      reportDetails.reported_users[0]?.id
    ) {
      learnerId = reportDetails.reported_users[0].id;
    }

    if (!learnerId) {
      return;
    }

    try {
      setChatLoading(true);
      const response = await axiosInstance.post(
        `/admin/generate-chat-transcript/${teacherId}/${learnerId}`
      );

      // Some backend responses return BOTH a `detail` message and a transcript URL.
      // If we key off `detail` first, the UI can incorrectly show "No chat exists".
      const detailText = response?.data?.detail || response?.data?.message;
      const transcriptUrl =
        response?.data?.url ||
        response?.data?.pdf_url ||
        response?.data?.data?.url ||
        response?.data?.data?.pdf_url;

      if (transcriptUrl) {
        setChatHistory({ pdfUrl: transcriptUrl });
      } else if (
        typeof detailText === 'string' &&
        detailText.toLowerCase().includes('no chat exists')
      ) {
        setChatHistory({ noChat: true });
      } else {
        setChatHistory({ noChat: true });
      }
    } catch (_) {
      setChatHistory({ noChat: true });
    } finally {
      setChatLoading(false);
    }
  }, [reportDetails]);

  useEffect(() => {
    fetchReportDetails();
    fetchConversionRate();
  }, [id, fetchReportDetails, fetchConversionRate]);

  useEffect(() => {
    if (reportDetails) {
      fetchChatHistory();
    }
  }, [reportDetails, fetchChatHistory]);

  const convertKoinsToRiyal = koins => {
    if (!riyalConversionRate) return null;
    return (koins * riyalConversionRate).toFixed(2);
  };

  const handleResolve = () => {
    if (!canManageDisputes()) {
      toast.dismiss();
      toast.error("You don't have permission to manage disputes");
      return;
    }
    setResolveOpen(true);
  };

  const handleReject = () => {
    if (!canManageDisputes()) {
      toast.dismiss();
      toast.error("You don't have permission to manage disputes");
      return;
    }
    setRejectOpen(true);
  };

  const handleOpenChatHistory = () => {
    if (chatHistory?.pdfUrl) {
      window.open(chatHistory.pdfUrl, '_blank');
    }
  };

  const getActionTargetFromDetails = () => {
    const userRole = (reportDetails?.user_role || '').toLowerCase();
    if (!reportDetails?.booking?.teacher?.id) {
      return null;
    }
    if (userRole === 'teacher') {
      const target = Array.isArray(reportDetails?.reported_users)
        ? reportDetails.reported_users[0]
        : null;
      if (target && target.id) {
        return { role: 'learner', user: target };
      }
      return null;
    }
    return {
      role: 'instructor',
      user: reportDetails.booking.teacher || null,
    };
  };

  const handleViewRestrictedAccount = () => {
    if (!uiActionTarget?.user?.id) return;
    if (uiActionTarget.role === 'instructor') {
      navigate(`/admin/users/instructor-details/${uiActionTarget.user.id}`);
    } else {
      navigate(`/admin/users/learner-details/${uiActionTarget.user.id}`);
    }
  };

  const handleReportTakeAction = () => {
    if (!canManageDisputes()) {
      toast.dismiss();
      toast.error("You don't have permission to manage disputes");
      return;
    }

    const target = getActionTargetFromDetails();
    if (!target || !target.user) {
      return;
    }

    setActionTarget(target);
    setOpenActionConfirm(true);
  };

  const handleConfirmAction = () => {
    if (!actionTarget) {
      setOpenActionConfirm(false);
      return;
    }
    if (actionTarget.role === 'instructor') {
      handleOpenTeacherDisable();
    } else if (actionTarget.role === 'learner' && actionTarget.user) {
      handleOpenStudentDisable(actionTarget.user);
    }
    setOpenActionConfirm(false);
  };

  const handleOpenTeacherDisable = () => {
    if (!reportDetails?.booking?.teacher?.id) return;
    if (!canManageDisputes()) {
      toast.dismiss();
      toast.error("You don't have permission to manage disputes");
      return;
    }
    setOpenTeacherDisableModal(true);
  };

  const handleOpenStudentDisable = user => {
    if (!user?.id) return;
    if (!canManageDisputes()) {
      toast.dismiss();
      toast.error("You don't have permission to manage disputes");
      return;
    }
    setStudentTarget(user);
    setOpenStudentDisableModal(true);
  };

  const submitAction = async (action, comment = '') => {
    if (!canManageDisputes()) {
      toast.dismiss();
      toast.error("You don't have permission to manage disputes");
      return;
    }

    try {
      const userRole = reportDetails?.user_role || '';
      const url = `/admin/report-session/${id}/action/${action}${userRole ? `?user_role=${userRole}` : ''}`;
      await axiosInstance.patch(url, { comment });
      fetchReportDetails();
      setResolveOpen(false);
      setRejectOpen(false);
    } catch (_) {
      toast.error(`Failed to ${action} report`);
    }
  };

  if (loading) {
    return (
      <Layout title="Klass Online | Report Details">
        <Box className="relative h-screen w-full overflow-hidden">
          <Box className="sticky top-0 left-0 right-0 z-[1000] bg-white pr-[10px]">
            <AdminNavbar />
          </Box>
          <Box className="h-[calc(100vh-64px)] overflow-auto relative bg-[#F9F9F9] rounded-lg mr-[10px]">
            <DisputeDetailsSkeleton />
          </Box>
        </Box>
      </Layout>
    );
  }

  if (error || !reportDetails) {
    return (
      <Layout title="Klass Online | Report Details">
        <Box className="relative h-screen w-full overflow-hidden">
          <Box className="sticky top-0 left-0 right-0 z-[1000] bg-white pr-[10px]">
            <AdminNavbar />
          </Box>
          <Box className="h-[calc(100vh-64px)] overflow-auto relative bg-[#F9F9F9] rounded-lg mr-[10px] flex justify-center items-center">
            <Typography variant="h6" color="error">
              {error || 'Report details not found'}
            </Typography>
          </Box>
        </Box>
      </Layout>
    );
  }

  const reporter = reportDetails.reporter || {};
  const reporterCity = reporter?.city;
  const reporterCountry = reporter?.country;

  const formattedDate = formatDateFull(reportDetails.created_at);

  const sessionTime = reportDetails.booking?.start_time
    ? formatDateFull(reportDetails.booking.start_time)
    : 'N/A';

  const endTime =
    reportDetails.booking?.start_time && reportDetails.booking?.length_in_minutes
      ? toLocalTime(reportDetails.booking.start_time)
          .add(reportDetails.booking.length_in_minutes, 'minute')
          .format('h:mm A')
      : 'N/A';

  const isInstructorDispute = ['teacher', 'instructor'].includes(
    (reportDetails.user_role || '').toLowerCase()
  );

  const total = reportDetails.booking?.session_koins || 0;
  const discount = 0;
  const taxes = 0;
  const refundTotal = total - discount + taxes;

  const uiActionTarget = getActionTargetFromDetails();
  const isActionTargetInactive = uiActionTarget?.user && uiActionTarget.user.is_active === false;

  const handleBreadcrumbClick = () => {
    if (fromReportTab) {
      navigate('/admin/payments/disputes', { state: { activeTab: 2 } });
    } else {
      navigate('/admin/payments/disputes');
    }
  };

  return (
    <Layout title="Klass Online | Report Details">
      <Box className="relative h-screen w-full overflow-hidden">
        <Box className="sticky top-0 left-0 right-0 z-[1000] bg-white pr-[10px]">
          <AdminNavbar />
        </Box>
        <Box className="h-[calc(100vh-64px)] overflow-auto relative bg-[#F9F9F9] rounded-lg mr-[10px]">
          <Box className="flex flex-col p-[1rem] gap-[18px]">
            <Box className="flex flex-col gap-[5px]">
              <Box className="flex items-center gap-2">
                <Typography className="pointer-text" onClick={handleBreadcrumbClick}>
                  Payments {'>'}
                  {'>'} Reports {'>'}
                  {'>'}
                </Typography>
                <Typography className="view-text">View</Typography>
              </Box>
              <div className="dispute-header">
                <Typography className="dispute-header-title">
                  Report ID {reportDetails?.id ? formatReportId(reportDetails.id) : '#N/A'}
                </Typography>
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    borderRadius: '20px',
                    border: `1px solid ${StatusColor[reportDetails.status]?.color || '#949DA6'}`,
                    backgroundColor: StatusColor[reportDetails.status]?.bgcolor || '#F9FAFB',
                    color: StatusColor[reportDetails.status]?.color || '#949DA6',
                    padding: '2px 8px',
                    maxWidth: 'fit-content',
                  }}
                >
                  <Box
                    sx={{
                      width: '6px',
                      height: '6px',
                      borderRadius: '50%',
                      backgroundColor: StatusColor[reportDetails.status]?.color || '#949DA6',
                    }}
                  />
                  <Typography
                    sx={{
                      fontFamily: 'Poppins',
                      fontSize: '12px',
                      fontStyle: 'normal',
                      fontWeight: 400,
                      lineHeight: 'normal',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {StatusColor[reportDetails.status]?.value || reportDetails.status}
                  </Typography>
                </Box>
              </div>
              <Typography variant="body2" color="textSecondary">
                {formattedDate}
              </Typography>
            </Box>
            <div className="details-section">
              <h3 className="details-title">Session Details</h3>

              <div className="info-grid">
                <div className="info-item">
                  <span className="info-label">Booking ID</span>
                  <span className="info-value">
                    {reportDetails.booking?.id
                      ? `#${reportDetails.booking.id.substring(0, 8).toUpperCase()}`
                      : 'N/A'}
                  </span>
                </div>
                <div className="info-item">
                  <span className="info-label">Session Title</span>
                  <span className="info-value">{reportDetails.booking?.title || 'N/A'}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Session Type</span>
                  <span className="info-value">
                    {formatText(reportDetails.booking?.session_type) || 'N/A'}
                  </span>
                </div>
                <div className="info-item">
                  <span className="info-label">Duration</span>
                  <span className="info-value">
                    {reportDetails.booking?.length_in_minutes
                      ? `${reportDetails.booking.length_in_minutes} minutes`
                      : 'N/A'}
                  </span>
                </div>
                <div className="info-item">
                  <span className="info-label">Session Koins</span>
                  <span className="info-value">
                    {reportDetails.booking?.session_koins || 0} Koins
                    {riyalConversionRate && reportDetails.booking?.session_koins && (
                      <span style={{ marginLeft: '8px', color: '#949DA6' }}>
                        ({convertKoinsToRiyal(reportDetails.booking.session_koins)} Riyal)
                      </span>
                    )}
                  </span>
                </div>
                <div className="info-item">
                  <span className="info-label">Subject</span>
                  <span className="info-value">{reportDetails.booking?.subject || 'N/A'}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Level of Teaching</span>
                  <span className="info-value">
                    {reportDetails.booking?.level_of_teaching
                      ? formatText(reportDetails.booking.level_of_teaching)
                      : 'N/A'}
                  </span>
                </div>
                <div className="info-item">
                  <span className="info-label">Session Time</span>
                  <span className="info-value">
                    {sessionTime} - {endTime}
                  </span>
                </div>
                <div className="info-item">
                  <span className="info-label">Instructor</span>
                  <span className="info-value">
                    {reportDetails.booking?.teacher?.name || 'N/A'}
                  </span>
                </div>
              </div>
            </div>

            <div className="details-section">
              <h3 className="details-title">Report Raised By</h3>
              <div className="info-grid">
                <div className="info-item">
                  <span className="info-label">Name</span>
                  <span className="info-value">{reporter?.name || 'N/A'}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">User Role</span>
                  <div
                    className="role-tag"
                    style={{
                      backgroundColor:
                        reportDetails.user_role === 'teacher'
                          ? UserRoleColor['Teacher'].bgcolor
                          : UserRoleColor['Student'].bgcolor,
                      color:
                        reportDetails.user_role === 'teacher'
                          ? UserRoleColor['Teacher'].color
                          : UserRoleColor['Student'].color,
                    }}
                  >
                    {reportDetails.user_role === 'teacher'
                      ? UserRoleColor['Teacher'].value
                      : UserRoleColor['Student'].value}
                  </div>
                </div>
                <div className="info-item">
                  <span className="info-label">Phone Number</span>
                  <span className="info-value">{reporter?.phone_no || 'N/A'}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Email</span>
                  <span className="info-value">{reporter?.email || 'N/A'}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Current Location</span>
                  <span className="info-value">
                    {(() => {
                      if (reporterCity && reporterCountry && reporterCity !== reporterCountry) {
                        return `${reporterCity}, ${reporterCountry}`;
                      }
                      if (reporterCity) {
                        return reporterCity;
                      }
                      if (reporterCountry) {
                        return reporterCountry;
                      }
                      return 'N/A';
                    })()}
                  </span>
                </div>
                <div className="info-item">
                  <span className="info-label">Session Type</span>
                  <span className="info-value">
                    {formatText(reportDetails.booking?.session_type) || 'N/A'}
                  </span>
                </div>
                <div className="info-item">
                  <span className="info-label">Instructor Type</span>
                  <span className="info-value">
                    {formatText(reportDetails.teacher_type) || 'N/A'}
                  </span>
                </div>
              </div>
            </div>

            {reportDetails.reported_users &&
              Array.isArray(reportDetails.reported_users) &&
              reportDetails.reported_users.length > 0 && (
                <div className="details-section">
                  <h3 className="details-title">Reported Users</h3>
                  <div className="info-grid">
                    {reportDetails.reported_users.map((reportedUser, index) => (
                      <React.Fragment key={reportedUser.id || index}>
                        <div className="info-item">
                          <span className="info-label">
                            {reportDetails.reported_users.length > 1
                              ? `User ${index + 1} Name`
                              : 'User Name'}
                          </span>
                          <span className="info-value">{reportedUser.name || 'N/A'}</span>
                        </div>
                        <div className="info-item">
                          <span className="info-label">
                            {reportDetails.reported_users.length > 1
                              ? `User ${index + 1} Email`
                              : 'Email'}
                          </span>
                          <span className="info-value">{reportedUser.email || 'N/A'}</span>
                        </div>
                        <div className="info-item">
                          <span className="info-label">
                            {reportDetails.reported_users.length > 1
                              ? `User ${index + 1} Phone Number`
                              : 'Phone Number'}
                          </span>
                          <span className="info-value">{reportedUser.phone_no || 'N/A'}</span>
                        </div>
                        <div className="info-item">
                          <span className="info-label">
                            {reportDetails.reported_users.length > 1
                              ? `User ${index + 1} Location`
                              : 'Location'}
                          </span>
                          <span className="info-value">
                            {reportedUser.city && reportedUser.country
                              ? `${reportedUser.city}, ${reportedUser.country}`
                              : reportedUser.city
                                ? reportedUser.city
                                : reportedUser.country
                                  ? reportedUser.country
                                  : 'N/A'}
                          </span>
                        </div>
                      </React.Fragment>
                    ))}
                  </div>
                </div>
              )}

            <div className="details-section">
              <h3 className="details-title">Report Details</h3>

              <div className="info-grid">
                <div className="info-item">
                  <span className="info-label">Reason</span>
                  <span className="info-value">{formatText(reportDetails.reason)}</span>
                </div>

                {reportDetails.description && (
                  <div className="info-item">
                    <span className="info-label">Description</span>
                    <span className="info-value">{reportDetails.description}</span>
                  </div>
                )}
              </div>
              <Box className="report-take-action-row">
                <Typography className="report-take-action-text">
                  {isActionTargetInactive && uiActionTarget?.role
                    ? `The linked ${
                        uiActionTarget.role === 'instructor' ? 'instructor' : 'learner'
                      } account has already been restricted. You can still view the account details.`
                    : 'Found this report concerning or critical? You can review and restrict the related instructor or learner account.'}
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={
                    isActionTargetInactive ? handleViewRestrictedAccount : handleReportTakeAction
                  }
                  disabled={!canManageDisputes() || !uiActionTarget?.user}
                  className="report-take-action-btn"
                  title={
                    !canManageDisputes()
                      ? "You don't have permission to manage disputes"
                      : isActionTargetInactive
                        ? 'Open restricted account details'
                        : 'Review and take action on the related account'
                  }
                >
                  {isActionTargetInactive ? 'View Account' : 'Take Action'}
                </Button>
              </Box>
            </div>

            <div className="details-section">
              <h3 className="details-title">Session Recording</h3>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, p: 2 }}>
                <SessionRecordings bookingId={reportDetails.booking?.id} />

                {reportDetails.file_url && (
                  <div
                    className="recording-item recording-clickable"
                    onClick={() => window.open(reportDetails.file_url, '_blank')}
                  >
                    <Box className="recording-icon">
                      <PictureAsPdfIcon sx={{ color: '#0A5EF3' }} />
                    </Box>
                    <span className="recording-text">Document</span>
                  </div>
                )}
              </Box>
            </div>

            <div className="details-section">
              <h3 className="details-title">Chat History</h3>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, p: 2 }}>
                {chatLoading ? (
                  <div className="chat-card">
                    <Box className="chat-icon-primary">
                      <Box className="chat-loading-dot" />
                    </Box>
                    <span className="chat-history-loading">Loading chat history...</span>
                  </div>
                ) : chatHistory?.noChat ? (
                  <div className="chat-card">
                    <Box className="chat-icon-error">
                      <ChatIcon sx={{ color: '#DC2626' }} />
                    </Box>
                    <span className="chat-history-no-chat">
                      No chat exists between the specified Learner and Instructor.
                    </span>
                  </div>
                ) : chatHistory?.pdfUrl ? (
                  <div className="chat-history-item" onClick={handleOpenChatHistory}>
                    <div className="chat-history-content">
                      <Box className="chat-icon-primary">
                        <ChatIcon sx={{ color: '#0A5EF3' }} />
                      </Box>
                      <span className="chat-history-text">Chat History</span>
                    </div>
                    <Button variant="outlined" size="small" className="chat-view-btn">
                      View
                    </Button>
                  </div>
                ) : null}
              </Box>
            </div>

            {reportDetails.status === 'pending' && (
              <Box sx={{ marginTop: '24px', marginBottom: '24px' }}>
                {!canManageDisputes() && (
                  <Box className="report-note-card">
                    <Typography className="report-note-text">
                      <strong>Note:</strong> You don't have permission to approve or deny disputes.
                      Only users with dispute management permissions can take action on pending
                      disputes.
                    </Typography>
                  </Box>
                )}
                <Box className="report-action-buttons">
                  <Button
                    variant="outlined"
                    disabled={!canManageDisputes()}
                    className={`report-outline-btn ${!canManageDisputes() ? 'report-btn-disabled' : ''}`}
                    onClick={handleReject}
                    title={
                      !canManageDisputes()
                        ? "You don't have permission to manage disputes"
                        : 'Deny report'
                    }
                  >
                    Deny
                  </Button>
                  <Button
                    variant="contained"
                    disabled={!canManageDisputes()}
                    className={`report-primary-btn ${!canManageDisputes() ? 'report-btn-disabled' : ''}`}
                    onClick={handleResolve}
                    title={
                      !canManageDisputes()
                        ? "You don't have permission to manage disputes"
                        : 'Approve report'
                    }
                  >
                    Approve
                  </Button>
                </Box>
              </Box>
            )}
          </Box>
        </Box>

        {openResolveModal && (
          <DisputeResolveModal
            setResolveOpen={setResolveOpen}
            onResolve={comment => submitAction('resolve', comment)}
            userRole={reportDetails.user_role || 'student'}
            isReport={true}
          />
        )}

        {openRejectModal && (
          <DisputeRejectModal
            setRejectOpen={setRejectOpen}
            onReject={comment => submitAction('reject', comment)}
            userRole={reportDetails.user_role || 'student'}
            isReport={true}
          />
        )}

        <ReportActionConfirmModal
          open={openActionConfirm}
          onClose={() => setOpenActionConfirm(false)}
          onConfirm={handleConfirmAction}
          actionTarget={actionTarget}
        />

        {openTeacherDisableModal && reportDetails.booking?.teacher?.id && (
          <TeacherEnableModal
            setOpenApproveModal={setOpenTeacherDisableModal}
            rowId={reportDetails.booking.teacher.id}
            teacherName={reportDetails.booking.teacher.name || ''}
            isActive={true}
            featureToToggle={null}
            isEnabling={false}
            currentFeatures={{}}
          />
        )}

        {openStudentDisableModal && studentTarget?.id && (
          <StudentEnableModal
            setOpenApproveModal={setOpenStudentDisableModal}
            rowId={studentTarget.id}
            studentName={studentTarget.name || ''}
            isActive={true}
            featureToToggle={null}
            isEnabling={false}
            currentFeatures={{}}
            onActionSuccess={fetchReportDetails}
          />
        )}
      </Box>
    </Layout>
  );
};

export default ReportDetails;
